import {
  Component,
  OnInit,
  Renderer2,
  OnDestroy,
  HostListener,
  ElementRef,
} from '@angular/core';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  constructor(private renderer: Renderer2, private elRef: ElementRef) {}

  showMobileMenu = false;

  currentYear = new Date().getFullYear();

  // Reimplementation of the ngx-headroom behaviour the template used before the
  // Angular 19 upgrade dropped the package. The three states drive the
  // .headroom--unfixed / --pinned / --unpinned rules that already exist in
  // _vien.style.scss: transparent bar with the white logo at the top of the
  // page, slide out of the way on the way down, slide back in as a white bar
  // with the dark logo on the way up.
  headroomState: 'unfixed' | 'pinned' | 'unpinned' = 'unfixed';
  headroomStyle: { [key: string]: string } = HomeComponent.styleFor('unfixed');

  // The template's own $navbar-height — the bar stays unfixed until it has
  // scrolled its own height out of view.
  private static readonly HEADROOM_OFFSET = 100;

  private lastScrollY = 0;

  // Once the bar has left the top it stays fixed — pinning and unpinning with
  // scroll direction — until the page is genuinely back at 0. Without this it
  // would flip back to transparent while scrolling up through the first 100px,
  // which the original did not do.
  private hasLeftTop = false;

  // Mirrors the inline styles ngx-headroom used to write on the .headroom element.
  private static styleFor(state: 'unfixed' | 'pinned' | 'unpinned'): {
    [key: string]: string;
  } {
    return {
      top: '0px',
      left: '0px',
      right: '0px',
      'z-index': '1',
      position: state === 'unfixed' ? 'relative' : 'fixed',
      transform: state === 'unpinned' ? 'translateY(-100%)' : 'translateY(0px)',
    };
  }

  private updateHeadroom(): void {
    const scrollY = window.scrollY;
    const delta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;

    if (scrollY > HomeComponent.HEADROOM_OFFSET) {
      this.hasLeftTop = true;
    }

    let next = this.headroomState;
    if (scrollY <= 0) {
      next = 'unfixed';
      this.hasLeftTop = false;
    } else if (!this.hasLeftTop) {
      next = 'unfixed';
    } else if (delta > 0) {
      next = 'unpinned';
    } else if (delta < 0) {
      next = 'pinned';
    }

    if (next !== this.headroomState) {
      this.headroomState = next;
      this.headroomStyle = HomeComponent.styleFor(next);
    }
  }


  services = [
    {
      icon: 'iconsminds-laptop-3',
      title: 'Software Development',
      detail:
        'Custom web applications, platforms and mobile apps, from first concept through to production.',
    },
    {
      icon: 'iconsminds-handshake',
      title: 'Software Consultancy',
      detail:
        'Strategic advice on technology decisions, embedded alongside your own team for as long as it helps.',
    },
    {
      icon: 'iconsminds-engineering',
      title: 'Technical Architecture',
      detail:
        'System design and technology choices that stay maintainable, and stay cheap to run.',
    },
    {
      icon: 'iconsminds-idea-2',
      title: 'Early-Phase Exploration',
      detail:
        'Prototyping and validating new concepts before the expensive commitments get made.',
    },
    {
      icon: 'iconsminds-puzzle',
      title: 'Problem Solving',
      detail:
        'The complex technical challenges others shy away from — where the answer is not yet on the shelf.',
    },
    {
      icon: 'iconsminds-pen',
      title: 'Product Design & UX',
      detail:
        'User experience design and research that turns a capable system into one people enjoy using.',
    },
    {
      icon: 'iconsminds-gears',
      title: 'Automation & Agents',
      detail:
        'Agent-driven development pipelines and automation that take the repetitive work off your desk.',
    },
    {
      icon: 'iconsminds-target',
      title: 'Quality Engineering',
      detail:
        'Testing, observability and release practice, so shipping often stays a calm and ordinary event.',
    },
  ];

  projects = [
    {
      title: 's3p.dev',
      subtitle: 'The vibe cloud',
      img: '/assets/img/projects/s3p.jpg',
      url: 'https://s3p.dev',
      detail:
        'A backendless application platform. Your agent writes the app; S3 is the hosting, the database and the auth. ' +
        'There is no server process to run — apps talk straight to AWS with per-user credentials, so an idle app costs ' +
        'practically nothing and still gets real sign-in, real permissions and serverless functions.<br /><br />' +
        'Built on AWS S3, Cognito and IAM, with DuckDB-WASM for analytics in the browser and optional in-browser ' +
        'envelope encryption on top of it.',
    },
    {
      title: 'bulletlist.org',
      subtitle: 'A simple list that holds your whole life',
      img: '/assets/img/projects/bulletlist.jpg',
      url: 'https://bulletlist.org',
      detail:
        'An infinitely nested outliner where every bullet is a page. Free — not freemium: no item cap, no trial, and ' +
        'a full demo that needs no account at all.<br /><br />' +
        'The keyboard shortcuts are deliberately the ones people already know, OPML goes in and out at any time, and ' +
        'the whole outline lives in S3. It runs on s3p, which makes it our own proof that the architecture holds up ' +
        'in a real product.',
    },
    {
      title: 'lostpetalarm.com',
      subtitle: 'Like an Amber Alert for pets',
      img: '/assets/img/projects/lostpetalarm.jpg',
      url: 'https://lostpetalarm.com',
      detail:
        'When a pet goes missing, a targeted social campaign reaches thousands of people exactly where it was lost — ' +
        'within thirty minutes.<br /><br />' +
        'Printable QR posters, instant sighting alerts and secure owner-to-finder messaging do the rest. Live across ' +
        'multiple countries and languages, with server-side rendering for search, automated ad buying against the ' +
        'Meta API, and Stripe for payments.',
    },
    {
      title: 'cadences.work',
      subtitle: 'Every team has a rhythm',
      img: '/assets/img/projects/cadences.jpg',
      url: 'https://cadences.work',
      detail:
        'A modern HR platform for growing teams, built around 360-degree feedback that people actually complete: ' +
        'self-nominated reviewers, review cycles, goal tracking, and AI synthesis that turns scattered peer input ' +
        'into a development plan.<br /><br />' +
        'Time tracking and HR administration are next. Next.js and Supabase underneath, with role-based access ' +
        'control and enterprise SSO.',
    },
  ];

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'no-footer');
    // Deep links and reloads can land mid-page, so seed the bar from the
    // current position rather than assuming the top.
    this.lastScrollY = window.scrollY;
    this.updateHeadroom();
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'no-footer');
  }

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    const homeRow = this.elRef.nativeElement.querySelector('.home-row');
    const footerSection = this.elRef.nativeElement.querySelector(
      '.landing-page .section.footer'
    );
    // The template also shifted the hero's background here. That offset was
    // sized for the original 1440px-tall hero, where `cover` blew the wedge up
    // to roughly 2600px wide; the hero is far shorter now, so the wedge renders
    // near viewport width and any shift just drags it off-screen. The footer is
    // untouched, so it keeps the original correction.
    if (homeRow && footerSection) {
      const homeRect = homeRow.getBoundingClientRect();
      footerSection.style.backgroundPositionX =
        event.target.innerWidth - homeRect.x - 2000 + 'px';
    }

    if (event.target.innerWidth >= 992) {
      this.renderer.removeClass(
        this.elRef.nativeElement.querySelector('.landing-page'),
        'show-mobile-menu'
      );
    }
  }

  @HostListener('window:click', ['$event'])
  onClick(event): void {
    this.showMobileMenu = false;
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event): void {
    this.showMobileMenu = false;
    this.updateHeadroom();
  }

  // ngx-scroll-to went away with the Angular 19 upgrade; native smooth
  // scrolling keeps the template's original -150px heading offset.
  scrollTo(target: string): void {
    const element = document.querySelector(target);
    if (element) {
      window.scrollTo({
        top: element.getBoundingClientRect().top + window.scrollY - 150,
        behavior: 'smooth',
      });
    }
  }
}
